import { along, length, lineString, nearestPointOnLine, point, distance as turfDistance } from "@turf/turf";
import { PlanTrailActionContext } from "./types";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../../../utils/models/MarkerFeature";
import { LineStringFeature, extrasWithSurfaces } from "../../../../../utils/models/LineStringFeature";
import { generateID } from "../../../../../utils/helpers/IDGenerator";
import { edgeValues, rangesForLine } from "../../../../../utils/models/segmentSurfaces";

export interface SpacedControlPoint {
  point: [number, number]; // [lng, lat]
  lineStringIndex: number;
  segmentIndex: number;
}

interface LineStringLike {
  lineStringGeometry: { coordinates: number[][] };
}

interface MarkerLike {
  markerGeometry: { coordinates: number[] };
}

interface SpacingOptions {
  segments?: number;
  skipRadiusMeters?: number;
}

const DEFAULT_SEGMENTS = 5;
const DEFAULT_SKIP_RADIUS_METERS = 500;

/**
 * Compute evenly-spaced control points along a route.
 *
 * Divides the route into `segments` equal parts (default 5) and returns the
 * interior division points (default 4) — e.g. a 10 km route yields points at
 * 2/4/6/8 km. Any target whose position already has a control point within
 * `skipRadiusMeters` (default 500 m) is skipped, so re-running on an already
 * populated route is effectively a no-op.
 *
 * The returned points are sorted *descending* by cumulative distance along the route.
 * This matters for the caller: inserting a point splits its linestring and shifts the
 * indices of everything after it, so inserting from the far end backwards keeps the
 * not-yet-inserted points' precomputed (lineStringIndex, segmentIndex) valid — even when
 * several points land in the same linestring or the same segment.
 */
export function computeEvenlySpacedControlPoints(
  lineStrings: LineStringLike[],
  existingMarkers: MarkerLike[],
  opts: SpacingOptions = {},
): SpacedControlPoint[] {
  const segments = opts.segments ?? DEFAULT_SEGMENTS;
  const skipRadius = opts.skipRadiusMeters ?? DEFAULT_SKIP_RADIUS_METERS;

  if (segments < 2 || !lineStrings || lineStrings.length === 0) return [];

  // Per-linestring prefix lengths in metres: prefix[i] is the cumulative distance
  // up to (and excluding) lineStrings[i]; prefix[last] is the total route length.
  const prefix: number[] = [0];
  for (const ls of lineStrings) {
    const coords = ls.lineStringGeometry?.coordinates;
    const len = coords && coords.length >= 2
      ? length(lineString(coords), { units: "meters" })
      : 0;
    prefix.push(prefix[prefix.length - 1] + len);
  }

  const total = prefix[prefix.length - 1];
  if (total <= 0) return [];

  const results: Array<{ cp: SpacedControlPoint; target: number }> = [];

  for (let k = 1; k < segments; k++) {
    const target = (total * k) / segments;

    // Find the linestring containing the target distance.
    let lineStringIndex = -1;
    for (let i = 0; i < lineStrings.length; i++) {
      if (target >= prefix[i] && target <= prefix[i + 1]) {
        lineStringIndex = i;
        break;
      }
    }
    if (lineStringIndex === -1) continue;

    const coords = lineStrings[lineStringIndex].lineStringGeometry?.coordinates;
    if (!coords || coords.length < 2) continue;

    const offset = target - prefix[lineStringIndex];
    const ls = lineString(coords);
    const interpolated = along(ls, offset, { units: "meters" });
    const [lng, lat] = interpolated.geometry.coordinates;

    // Skip if an existing control point is already near this position.
    const tooClose = existingMarkers.some((m) => {
      const c = m.markerGeometry?.coordinates;
      if (!c || c.length < 2) return false;
      return turfDistance([c[0], c[1]], [lng, lat], { units: "meters" }) < skipRadius;
    });
    if (tooClose) continue;

    const segmentIndex = nearestPointOnLine(ls, point([lng, lat])).properties.index as number;

    results.push({ cp: { point: [lng, lat], lineStringIndex, segmentIndex }, target });
  }

  // Descending cumulative distance so reverse-order insertion keeps precomputed indices valid.
  results.sort((a, b) => b.target - a.target);

  return results.map((r) => r.cp);
}

interface MutableRoute {
  markers: MarkerFeature[];
  lineStrings: LineStringFeature[];
}

/**
 * Bake evenly-spaced control points directly into a route's `markers` / `lineStrings`,
 * synchronously and without any network calls or undo-stack entries.
 *
 * This is the import-time counterpart to the interactive `addEvenlySpacedControlPoints`
 * action (which goes through `insertMarkerAtPoint`, fetching elevation and pushing an undo
 * command per point). On import we have many points across potentially many routes and the
 * source coords already carry elevation, so we split in-memory and interpolate the inserted
 * point's elevation from its neighbours — mirroring the offline fallback in
 * `map-interactions.ts`.
 *
 * Returns the number of control points inserted.
 */
export function insertEvenlySpacedControlPoints(
  route: MutableRoute,
  opts: SpacingOptions = {},
): number {
  if (!route || !route.lineStrings || route.lineStrings.length === 0) return 0;

  // Sorted descending by distance, so each split leaves the not-yet-inserted points'
  // precomputed (lineStringIndex, segmentIndex) valid — even within the same linestring.
  const points = computeEvenlySpacedControlPoints(route.lineStrings, route.markers, opts);

  let inserted = 0;
  for (const pt of points) {
    const lineStringIndex = pt.lineStringIndex;
    const lineToSplit = route.lineStrings[lineStringIndex];
    const coords = lineToSplit?.lineStringGeometry?.coordinates;
    if (!coords || coords.length < 2) continue;

    const [lng, lat] = pt.point;
    const hasElevation = coords[0].length > 2;

    const coords1: number[][] = [];
    const coords2: number[][] = [];
    for (let i = 0; i <= pt.segmentIndex; i++) coords1.push(coords[i]);

    // Interpolate the inserted point's elevation from the two adjacent track coords.
    const before = coords[pt.segmentIndex];
    const after = coords[pt.segmentIndex + 1] ?? before;
    const z1 = before?.[2] ?? 0;
    const z2 = after?.[2] ?? z1;
    const ele = (z1 + z2) / 2;

    const insertCoord1 = hasElevation ? [lng, lat, ele] : [lng, lat];
    const insertCoord2 = [...insertCoord1];
    coords1.push(insertCoord1);
    coords2.push(insertCoord2);
    for (let i = pt.segmentIndex + 1; i < coords.length; i++) coords2.push(coords[i]);

    if (coords1.length < 2 || coords2.length < 2) continue;

    const newLS1 = LineStringFeature.coordsOnlyFeature(coords1);
    const newLS2 = LineStringFeature.coordsOnlyFeature(coords2);
    // The new point cuts edge `segmentIndex` in two, so both halves keep its surface.
    const extras = lineToSplit.lineStringProperties?.extras;
    const surfaces = edgeValues(extras?.surface?.values, coords.length);
    newLS1.lineStringProperties = {
      ...lineToSplit.lineStringProperties,
      id: generateID(),
      extras: extrasWithSurfaces(extras, rangesForLine(coords1, surfaces.slice(0, pt.segmentIndex + 1))),
    };
    newLS2.lineStringProperties = {
      ...lineToSplit.lineStringProperties,
      id: generateID(),
      extras: extrasWithSurfaces(extras, rangesForLine(coords2, surfaces.slice(pt.segmentIndex))),
    };
    route.lineStrings.splice(lineStringIndex, 1, newLS1, newLS2);

    const marker = new MarkerFeature(
      new MarkerGeometry(hasElevation ? [lng, lat, ele] : [lng, lat]),
      new MarkerProperties(generateID(), true, Date.now().toString(), hasElevation ? ele : 0),
    );
    route.markers.splice(lineStringIndex + 1, 0, marker);

    inserted++;
  }

  return inserted;
}

export const controlPointSpacingActions = {
  async addEvenlySpacedControlPoints({ getters, dispatch }: PlanTrailActionContext) {
    const route = getters.activeRoute;
    if (!route || !route.lineStrings || route.lineStrings.length === 0) return;

    const points = computeEvenlySpacedControlPoints(route.lineStrings, route.markers);

    for (const pt of points) {
      await dispatch("insertMarkerAtPoint", {
        point: pt.point,
        routeId: route.id,
        insertIndex: pt.lineStringIndex + 1,
        segmentIndex: pt.segmentIndex,
      });
    }
  },
};
